import { diffWords, type DiffToken } from '@/lib/wordDiff';

/**
 * Was ein Optimierungslauf an einem Produkt getan hat, als benannte Marker.
 *
 * Bewusst eine reine Funktion ohne React, ohne Netzzugriff und ohne KI: alles,
 * was hier gebraucht wird, liegt bereits im Werkbank-Query. Der Wort-Diff wird
 * fuer die Anzeige ohnehin gerechnet, der Attributvergleich steht schon im
 * Attribut-Tab. Ein LLM-Aufruf braeuchte es erst fuer eine frei formulierte
 * Begruendung je Produkt, und die ist nicht das Ziel.
 *
 * Die Begruendungssaetze sind feste Textbausteine je Marker (i18n), nur die
 * eingesetzten Werte sind produktabhaengig.
 */

export const ATTRIBUT_FELDER = [
  'color', 'material', 'gender', 'age_group', 'size', 'pattern', 'google_product_category',
] as const;

export type AttributFeld = (typeof ATTRIBUT_FELDER)[number];

export interface AttributPaar {
  feld: AttributFeld;
  original: string | null;
  optimiert: string | null;
}

export interface ChangeInput {
  /** products.original_title, NICHT products.title; null = kein Original bekannt. */
  title: string | null;
  optimizedTitle: string | null;
  brand: string | null;
  attribute: AttributPaar[];
}

export type ChangeMarker =
  | { id: 'produktart_vorn'; alt: string; neu: string }
  | { id: 'marke_vorn'; marke: string }
  | { id: 'attribut_ergaenzt'; felder: AttributFeld[]; werte: string[] }
  | { id: 'masz_ergaenzt'; treffer: string }
  | { id: 'floskel_entfernt'; beispiel: string };

/**
 * Hoechstens vier Marker. Heute ist das keine echte Klemme, sondern eine
 * Leitplanke: mehr als vier koennen zurzeit gar nicht gleichzeitig zutreffen
 * (produktart_vorn und marke_vorn schliessen sich aus). Kommt eine Regel dazu,
 * greift der Deckel ohne weiteres Zutun.
 */
export const MAX_MARKER = 4;

function erstesWort(text: string): string {
  return text.trim().split(/\s+/)[0] ?? '';
}

/**
 * Ein ausgetauschtes erstes Wort: aus "Hoodie" wird "Kapuzenpullover". Der Diff
 * liefert dafuer genau ein `removed` unmittelbar gefolgt von einem `added` an
 * Position 0 (aufeinanderfolgende gleiche Operationen fasst diffWords zusammen).
 */
function produktartVorn(tokens: DiffToken[], brand: string | null): ChangeMarker | null {
  if (tokens[0]?.op !== 'removed' || tokens[1]?.op !== 'added') return null;
  const alt = erstesWort(tokens[0].text);

  /**
   * Steht die MARKE vorn, ist sie nicht die neue Produktart.
   *
   * "Hoodie Herren" -> "Talholz Kapuzenpullover Herren" liefert
   * removed("Hoodie") + added("Talholz Kapuzenpullover"). Das erste ergaenzte
   * Wort ist dann die Marke, und die Karte meldete "Aus Hoodie wird Talholz" -
   * eine Falschaussage ueber genau die Aenderung, die geprueft werden soll.
   * Die Marke wird deshalb uebersprungen; ihr eigener Marker (`marke_vorn`)
   * berichtet sie getrennt.
   */
  const marke = (brand ?? '').trim();
  const ergaenzt = tokens[1].text.trim().split(/\s+/).filter((w) => w !== '');
  let rest = ergaenzt;
  if (marke !== '') {
    const markenWorte = marke.split(/\s+/).filter((w) => w !== '');
    const passt = markenWorte.every(
      (w, i) => (ergaenzt[i] ?? '').toLocaleLowerCase('de') === w.toLocaleLowerCase('de'),
    );
    if (passt) rest = ergaenzt.slice(markenWorte.length);
  }

  const neu = rest[0] ?? '';
  if (!alt || !neu) return null;
  // Der Diff vergleicht case-sensitiv (wordDiff.ts:key()). Ohne diese Pruefung
  // wuerde "hoodie" -> "Hoodie" hier als ausgetauschte Produktart gemeldet,
  // obwohl es dieselbe Produktart in sauberer Schreibung ist.
  if (alt.toLocaleLowerCase('de') === neu.toLocaleLowerCase('de')) return null;
  return { id: 'produktart_vorn', alt, neu };
}

/**
 * Ein ergaenztes erstes Wort, das der Produktmarke entspricht.
 *
 * Der erste Block darf `added` ODER `removed` gefolgt von `added` sein. Nur
 * den reinen `added`-Fall zu pruefen war zu eng: sobald im selben Zug auch die
 * Produktart getauscht wurde ("Hoodie" -> "Talholz Kapuzenpullover"), steht
 * vorn ein `removed`, und der Marker konnte strukturell nie feuern - obwohl
 * die Marke sehr wohl vorangestellt wurde.
 */
function markeVorn(tokens: DiffToken[], brand: string | null): ChangeMarker | null {
  const marke = (brand ?? '').trim();
  if (marke === '') return null;

  const ersterZusatz =
    tokens[0]?.op === 'added' ? tokens[0]
    : tokens[0]?.op === 'removed' && tokens[1]?.op === 'added' ? tokens[1]
    : null;
  if (!ersterZusatz) return null;

  const zusatz = ersterZusatz.text.trim();
  if (!zusatz.toLowerCase().startsWith(marke.toLowerCase())) return null;
  // Schreibweise des TITELS, nicht der DB-Rohwert: brand "majorette" +
  // Titel "Majorette ..." meldete sonst "beginnt jetzt mit majorette"
  // direkt neben dem gross geschriebenen Titel (Befund 24.08.2026).
  return { id: 'marke_vorn', marke: zusatz.slice(0, marke.length) };
}

/**
 * Werbefloskeln, deren Entfernung erwaehnenswert ist.
 *
 * Bewusst kurz und bewusst ohne Sachaussagen: "versandkostenfrei",
 * "sofort lieferbar" und "neu" sind Informationen, keine Floskeln. Heute gibt
 * es nur diese deutsche Liste. Eine englische Liste wird, wenn sie kommt,
 * eine eigene Liste sein (keine Uebersetzung dieser hier) - und braucht dafuer
 * zusaetzlich eine Sprachauswahl, die `ChangeInput` bisher nicht kennt.
 */
const FLOSKELN_DE = [
  'jetzt', 'guenstig', 'günstig', 'bestellen', 'kaufen',
  'top', 'super', 'beste', 'angebot', 'sale',
];

/**
 * Zahl plus Einheit. Die Ziffer ist Pflicht, sonst trifft das Muster Fliesstext.
 *
 * Case-sensitiv und die Einheiten in kanonischer Schreibung (kg, mAh, ml, cm,
 * mm, g, l, W): mit einem `i`-Flag wuerde die Alternative "g" auch auf das "G"
 * in "5G" passen und eine Netzstandard-Angabe als ergaenztes Gewicht melden.
 * Bewusst in Kauf genommen: ein Merchant-Wert wie "320 G" wird dadurch nicht
 * mehr erkannt. Falsch negativ ist hier billiger als falsch positiv.
 *
 * Der Treffer schliesst NICHT mit `\b` ab, sondern mit einer negativen
 * Vorausschau: nach der echten Unicode-Hochzahl "m²" ist `\b` keine Wortgrenze
 * (weder "²" noch das folgende Leerzeichen ist ein Wortzeichen, ein Uebergang
 * Nichtwort->Nichtwort ist keine Grenze), der Motor backtrackt dann bis zur
 * kurzen Alternative "g" und meldet "320 g" statt "320 g/m²" - sachlich falsch
 * fuer ein Flaechengewicht. Die Vorausschau verlangt stattdessen direkt, dass
 * kein Buchstabe/keine Ziffer folgt, und haelt das auch nach einem
 * Nicht-Wortzeichen wie "²".
 */
const MASZ_MUSTER = /\d+(?:[.,]\d+)?\s?(?:g\/m2|g\/m²|kg|mAh|ml|cm|mm|g|l|W)(?![A-Za-z0-9])/;

export function istErgaenzt(paar: AttributPaar): boolean {
  const optimiert = (paar.optimiert ?? '').trim();
  if (optimiert === '') return false;
  const original = (paar.original ?? '').trim();
  // Eine reine Schreibungsaenderung ("navy" -> "Navy") ist keine inhaltliche
  // Ergaenzung. Das weicht bewusst vom frueheren isEnriched in AttributesTab
  // ab (mit Etappe 3 durch AttributeChips ersetzt): dort setzte der Vergleich
  // nur einen Punkt an eine Tabellenzeile, hier wird daraus ein Satz ueber das
  // Produkt ("Farbe ergaenzt") - und der waere falsch.
  return original === '' || original.toLocaleLowerCase('de') !== optimiert.toLocaleLowerCase('de');
}

function maszErgaenzt(tokens: DiffToken[]): ChangeMarker | null {
  for (const token of tokens) {
    if (token.op !== 'added') continue;
    const treffer = token.text.match(MASZ_MUSTER);
    if (treffer) return { id: 'masz_ergaenzt', treffer: treffer[0].trim() };
  }
  return null;
}

function floskelEntfernt(tokens: DiffToken[]): ChangeMarker | null {
  for (const token of tokens) {
    if (token.op !== 'removed') continue;
    for (const wort of token.text.toLowerCase().split(/\s+/)) {
      const nackt = wort.replace(/[^\p{L}\p{N}]/gu, '');
      if (nackt !== '' && FLOSKELN_DE.includes(nackt)) {
        // Hoechstens sechs Woerter: der entfernte Block kann ein halber Satz sein.
        const beispiel = token.text.trim().split(/\s+/).slice(0, 6).join(' ');
        return { id: 'floskel_entfernt', beispiel };
      }
    }
  }
  return null;
}

export function deriveChanges(input: ChangeInput): ChangeMarker[] {
  // Ohne Original gibt es kein Vorher und damit keinen Titel-Diff: jeder Token
  // waere "ergaenzt", und marke_vorn feuerte auf jedem Produkt mit Marke.
  // Spec Datenintegritaet Original-Titel 5.4: Titel-Marker nur mit Original.
  const hatOriginal = input.title !== null && input.title.trim() !== '';
  const tokens = hatOriginal ? diffWords(input.title ?? '', input.optimizedTitle ?? '') : [];
  const ergaenzte = input.attribute.filter(istErgaenzt);
  const felder = ergaenzte.map((paar) => paar.feld);
  // Die WERTE mitfuehren, nicht nur die Feldnamen: das Mockup schreibt
  // "Farbe und Groesse ergaenzt - Navy, Gr. L", und ohne die Werte sagt die
  // Zeile nur DASS etwas ergaenzt wurde, nicht WOMIT. Der Nutzer muesste zum
  // Pruefen zu den Chips darunter springen.
  const werte = ergaenzte.map((paar) => (paar.optimiert ?? '').trim());

  // Reihenfolge ist Anzeigereihenfolge: erst der Titelanfang, dann was ergaenzt
  // wurde, zuletzt was weggefallen ist.
  const marker: Array<ChangeMarker | null> = [
    hatOriginal ? produktartVorn(tokens, input.brand) : null,
    hatOriginal ? markeVorn(tokens, input.brand) : null,
    felder.length > 0 ? { id: 'attribut_ergaenzt', felder, werte } : null,
    hatOriginal ? maszErgaenzt(tokens) : null,
    hatOriginal ? floskelEntfernt(tokens) : null,
  ];

  return marker.filter((m): m is ChangeMarker => m !== null).slice(0, MAX_MARKER);
}
